// セレクター関数 - APIレスポンスからデータを抽出・変換
import type {
  ListSelfReportTargetsResponse,
  ListSelfReportsResponse,
} from "../../_root/packages/-components/DeviceInventoryOperation/query/type";
import type { DeviceInventoryDevice } from "../../_root/packages/-components/DeviceInventoryOperation/type";
import {
  listOperationPackages,
  listPackagePatterns,
} from "../../_root/packages/-query/api";
import type {
  ItOperationPackage,
  ItPackagePattern,
} from "../../_root/packages/-query/type";
import {
  convertSelfReportResultToApiDevice,
  convertSelfReportTargetToApiDevice,
  mapApiDevicesToDevices,
} from "./mappers";
import type { ApiDeviceResult } from "./types";

// 型定義・型ガード・バリデーション・マッパーを再エクスポート
export {
  type ApiDeviceInfo,
  type ApiDeviceResult,
  type ApiSelfReportData,
  type ApiSelfReportResult,
  type ApiSelfReportTarget,
} from "./types";
export { isSelfReportResult, isSelfReportTarget } from "./typeGuards";
export {
  ValidationError,
  safeConvertToId,
  validateAnchorDate,
  validateDeviceCondition,
  validateDeviceId,
  validatePatternId,
  validatePlacementVerificationStatus,
  validateRequestId,
} from "./validators";
export {
  convertSelfReportResultToApiDevice,
  convertSelfReportTargetToApiDevice,
  determineDeviceStatus,
  mapApiDeviceToDevice,
  mapApiDevicesToDevices,
  mapApiStatusToReportStatus,
} from "./mappers";

/**
 * APIレスポンスから型安全にデバイスリストを変換
 */
export function convertApiResponseToDevices(
  responseData: ListSelfReportsResponse | ListSelfReportTargetsResponse,
  isSelfReportCompleted: boolean,
): DeviceInventoryDevice[] {
  try {
    let apiDevices: ApiDeviceResult[] = [];

    if (isSelfReportCompleted) {
      // セルフレポート結果の場合
      const response = responseData as ListSelfReportsResponse;
      const resultsArray = response.items || [];

      apiDevices = resultsArray
        .map((result) => convertSelfReportResultToApiDevice(result))
        .filter((device): device is ApiDeviceResult => device !== null);
    } else {
      // セルフレポート対象の場合
      const response = responseData as ListSelfReportTargetsResponse;
      const targetsArray = response.items || [];

      apiDevices = targetsArray
        .map((target) => convertSelfReportTargetToApiDevice(target))
        .filter((device): device is ApiDeviceResult => device !== null);
    }

    return mapApiDevicesToDevices(apiDevices);
  } catch (error) {
    console.error("Failed to convert API response to devices:", error);
    return [];
  }
}

// ===== パッケージ情報取得 =====

/**
 * パッケージIDとパターン情報を取得
 */
export async function getPackageInfo(patternId: string): Promise<{
  packageId: string;
  packageInfo: ItOperationPackage;
  patternInfo: ItPackagePattern;
} | null> {
  try {
    // 全パッケージを取得
    const allPackages = await listOperationPackages();

    // パフォーマンス最適化: 並列でパターンをチェック
    const packageChecks: Promise<{
      packageId: string;
      packageInfo: ItOperationPackage;
      patternInfo: ItPackagePattern;
    } | null>[] = [];

    for (const [, packages] of Object.entries(allPackages)) {
      for (const pkg of packages) {
        packageChecks.push(findPatternInPackage(pkg, patternId));
      }
    }

    // 並列実行で最初にマッチしたものを返す
    const results = await Promise.allSettled(packageChecks);

    for (const result of results) {
      if (result.status === "fulfilled" && result.value) {
        return result.value;
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * パッケージ内でパターンを検索
 */
async function findPatternInPackage(
  pkg: ItOperationPackage,
  patternId: string,
): Promise<{
  packageId: string;
  packageInfo: ItOperationPackage;
  patternInfo: ItPackagePattern;
} | null> {
  try {
    const patterns = await listPackagePatterns(pkg.id);
    const foundPattern = patterns.find(
      (p) => p.operationPackagePatternId === patternId,
    );

    if (foundPattern) {
      return {
        packageId: pkg.id,
        packageInfo: pkg,
        patternInfo: foundPattern,
      };
    }

    return null;
  } catch {
    return null;
  }
}
